<template>
  <v-app>
    <Callback v-if="onCallbackPath" />
    <Unavailable v-else-if="session.status === 'unavailable'" />
    <NoAccess v-else-if="session.status === 'no-access'" />
    <OrgPicker v-else-if="session.status === 'org-pick'" />
    <Login v-else-if="session.status === 'signed-out' || session.status === 'error'" />
    <template v-else-if="session.status === 'ready'">
      <TopBar />
      <Sidebar v-model:rail="rail" />
    </template>
    <!-- single-spa mounts the tenant's modules straight onto document.body
         (single-spa-vue's default target — no host container needed);
         modules pad themselves below via --metaship-header-height. -->
    <div v-else class="standalone-loading" role="status" aria-label="Loading">
      <span class="dot"></span>
      <span class="dot"></span>
      <span class="dot"></span>
    </div>
  </v-app>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, ref, watch } from "vue";
import { useSessionStore } from "@/stores/session";
import Sidebar from "./Sidebar.vue";
import TopBar from "./TopBar.vue";
import Login from "./views/Login.vue";
import Callback from "./views/Callback.vue";
import NoAccess from "./views/NoAccess.vue";
import Unavailable from "./views/Unavailable.vue";
import OrgPicker from "./views/OrgPicker.vue";

const RAIL_STORAGE_KEY = "metaship:standalone:railCollapsed";

export default defineComponent({
  name: "App",
  components: { Sidebar, TopBar, Login, Callback, NoAccess, Unavailable, OrgPicker },
  setup() {
    const session = useSessionStore();
    const path = ref(window.location.pathname);
    // `history.replaceState` (Callback.vue clears the URL before its async
    // work) does NOT fire `popstate`, so `path` alone can stay stuck on
    // "/auth0/callback" long after the URL itself has moved on. Gate on
    // `session.status` too — every status the callback flow can resolve to
    // (ready/org-pick/no-access/error) must swap away from the spinner even
    // when nothing yet caused a popstate/single-spa routing event.
    const onCallbackPath = computed(
      () => path.value === "/auth0/callback" && (session.status === "authenticating" || session.status === "resolving"),
    );

    const rail = ref(readRailPreference());
    watch(rail, (value) => {
      try {
        localStorage.setItem(RAIL_STORAGE_KEY, value ? "1" : "0");
      } catch {
        // best-effort
      }
      publishNavWidth(value);
    });

    function onRoutingEvent(): void {
      path.value = window.location.pathname;
    }

    onMounted(() => {
      publishNavWidth(rail.value);
      document.documentElement.style.setProperty("--metaship-footer-height", "0px");
      // Matches TopBar's `density="compact"` app-bar height exactly (Vuetify's
      // compact app-bar is 48px) — the single source of truth every hosted
      // module's `padding-top: var(--metaship-header-height)` reads.
      document.documentElement.style.setProperty("--metaship-header-height", "48px");
      window.addEventListener("popstate", onRoutingEvent);
      window.addEventListener("single-spa:before-routing-event", onRoutingEvent);
    });
    onUnmounted(() => {
      window.removeEventListener("popstate", onRoutingEvent);
      window.removeEventListener("single-spa:before-routing-event", onRoutingEvent);
    });

    return { session, onCallbackPath, rail };
  },
});

function readRailPreference(): boolean {
  try {
    return localStorage.getItem(RAIL_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function publishNavWidth(rail: boolean): void {
  document.documentElement.style.setProperty("--metaship-nav-width", rail ? "56px" : "230px");
}
</script>
